import { AxiosResponse } from "axios";

import {
  IAddHospitalRequest,
  IAddTenantRequest,
  IHospital,
  ISyncTenantIdsResult,
  ITenant,
  ITenantStylesResponse,
  IUpdateTenantRequest,
  IUpdateTenantStylesRequest,
} from "../../@types/admin/AdminHospital";
import { IPaginatedResponse } from "../../@types/PaginatedResponse";
import { buildUploadFormData } from "../../utils/fileUpload";
import { api } from "../api";
import { fileTransferService } from "./fileTransferService";

/**
 * Hospitals admin page (EN-5152), migrated from the Retool Hospitals app.
 * A hospital is a `cms_hospitalgroup`; its wards live on the Wards page.
 */
export const hospitalsService = {
  async getHospitals(): Promise<
    AxiosResponse<IPaginatedResponse<IHospital[]>>
  > {
    return api.get<IPaginatedResponse<IHospital[]>>("/Retool/Hospitals");
  },

  /** Tenant catalogue, used to name the Tenant column and fill the create select. */
  async getTenants(): Promise<AxiosResponse<IPaginatedResponse<ITenant[]>>> {
    return api.get<IPaginatedResponse<ITenant[]>>("/Retool/Tenants");
  },

  async addTenant(request: IAddTenantRequest): Promise<AxiosResponse<ITenant>> {
    return api.post<ITenant>("/Retool/Tenants", request);
  },

  async updateTenant(
    request: IUpdateTenantRequest
  ): Promise<AxiosResponse<ITenant>> {
    return api.put<ITenant>(`/Retool/Tenants/${request.id}`, request);
  },

  async getTenantById(tenantId: string): Promise<AxiosResponse<ITenant>> {
    return api.get<ITenant>(`/Retool/Tenants/${tenantId}`);
  },

  /**
   * A tenant's saved theme. Answers 204 with no body when the tenant has
   * never been themed, which the caller reads as "use the defaults".
   */
  async getTenantStyles(
    tenantId: string
  ): Promise<AxiosResponse<ITenantStylesResponse | null>> {
    return api.get<ITenantStylesResponse | null>(
      `/Retool/Tenants/Styles/${tenantId}`
    );
  },

  async updateTenantStyles(
    request: IUpdateTenantStylesRequest
  ): Promise<AxiosResponse<ITenant>> {
    return api.put<ITenant>(
      `/Retool/Tenants/Styles/${request.tenantId}`,
      request
    );
  },

  /**
   * Replaces a tenant's logo. Multipart: the API binds `Image` + `TenantId`
   * from the form, and rejects a mismatch against the route id.
   */
  async uploadTenantLogo(
    tenantId: string,
    image: File
  ): Promise<AxiosResponse<void>> {
    return fileTransferService.upload<void>({
      url: `/Retool/Tenants/Logo/${tenantId}`,
      method: "PUT",
      formData: buildUploadFormData({ Image: image, TenantId: tenantId }),
    });
  },

  /**
   * Removes a tenant's logo. Retool sent a form-data body here, but the
   * endpoint takes the id from the route only.
   */
  async deleteTenantLogo(tenantId: string): Promise<AxiosResponse<void>> {
    return api.delete<void>(`/Retool/Tenants/Logo/${tenantId}`);
  },

  async addHospital(
    request: IAddHospitalRequest
  ): Promise<AxiosResponse<IHospital>> {
    return api.post<IHospital>("/Retool/Hospitals", request);
  },

  /**
   * Pushes a hospital group's tenant id onto every Cognito user under it.
   * A bulk write over live accounts — always confirm before calling.
   */
  async syncTenantIds(
    hospitalGroupId: number
  ): Promise<AxiosResponse<ISyncTenantIdsResult>> {
    return api.post<ISyncTenantIdsResult>("/Retool/Users/SyncAllTenantIds", {
      hospitalGroupId,
    });
  },
};
